import { UploadedFile } from '@components/file-field';
import { DefaultFile } from '@components/file-field/types';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { HtmlContent } from '@components/html-content';
import { IconEmail } from '@components/icons/email';
import { IconSend } from '@components/icons/send';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingVariables,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { getRawTextFromHtml } from '@utils/browser';
import { fileNameFromPath } from '@utils/strings';

import { createTravellerJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { PreviewEmailProps, usePreviewEmailModal } from './email-preview-modal';

import {
  ActionResultCard,
  AttachmentList,
  EditableActionContent,
  FormSidebarFooter,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'send_email';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('sendEmail')} icon={IconEmail} iconColor="green100">
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  return (
    <Stack space={4}>
      <LabelTextGroup label={t('description')} text={action.description} />
      <EmailDetailCard action={action} />
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('sendEmail')}
        retryText={t('resendEmail')}
      />
    </Stack>
  );
};

type EmailData = {
  description: string;
  isAutomatic: boolean;
  recipients: EditorValue<{ variables?: FieldMappingVariables }>[];
  subject: EditorValue<{ variables?: FieldMappingVariables }>;
  content: EditorValue<{ variables?: FieldMappingVariables }>;
  attachments: EditorValue<{ variables?: FieldMappingVariables }>;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
};

const Form = createForm<EmailData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['email'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['file', 'file_multi'],
  });
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const uploadMutation = useUploadFile(createTravellerJourneyActionDocumentPath(action), true);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const handleSave = (data: EmailData) => {
    return editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        recipients: data.recipients ?? [],
        subject: data.subject?.value ? data.subject : {},
        content: data.content ?? {},
        attachments: data.attachments ?? [],
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            hideLabel
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('emailContent')}>
          <Stack space={4}>
            <Form.EmailListField
              id="recipients"
              label={t('sendTo')}
              defaultValue={action.values?.recipients ?? []}
              variableGroups={emailVariableGroups}
              validators={['required']}
              info={t('sendToInfo')}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingTextEditor
              id="subject"
              label={t('subject')}
              plaintext
              singleLine
              variableGroups={baseVariableGroups}
              defaultValue={action.values?.subject}
              validators={['required']}
              info={t('subjectInfo')}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingTextEditor
              id="content"
              label={t('messageBody')}
              variableGroups={baseVariableGroups}
              defaultValue={action.values?.content}
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingFileListField
              id="attachments"
              label={t('emailAttachment')}
              defaultValue={action.values?.attachments}
              variableGroups={fileVariableGroups}
              onSelect={uploadMutation.mutateAsync}
              fieldMappingId={fieldMappingId}
            />
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

type Attachment = Omit<DefaultFile, 'id'> & {
  canView?: boolean;
};

export const EmailDetailCard = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showPreviewModal = usePreviewEmailModal();

  const recipients = ((action.data?.recipients ?? []) as string[]).flatMap((receipient) =>
    receipient.split(',')
  );
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const showFilePreviewModal = usePreviewFileModal();

  const progressText =
    status === 'completed'
      ? t('delivered')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notSent');

  const attachmentsValues = (action.values?.attachments ?? []).map((att: VariableValue) =>
    att.variables ? Object.values(att.variables)[0].label : att.value
  );

  const emailMessage: PreviewEmailProps = {
    action: 'traveller',
    shortId: action.shortId,
    to: recipients,
    content: action.savedValues?.fullHtml ?? action.data?.content ?? undefined,
    subject: action.data?.subject ?? undefined,
    attachments: attachmentsValues,
  };

  const actionDataAttachments: Omit<DefaultFile, 'id'>[] = (action.data?.attachments ?? []).flat();

  const attachments: Attachment[] = actionDataAttachments.map((att: Omit<DefaultFile, 'id'>) => {
    if (typeof att == 'string') {
      return { name: fileNameFromPath(att), src: att, canView: false };
    }
    return { ...att, canView: true };
  });
  const hasAttachments =
    (status === 'completed' ? attachments.length : attachmentsValues.length) > 0;

  return (
    <ActionResultCard
      action={action}
      title={t('emailMessage')}
      icon={<IconEmail />}
      progressText={progressText}
      errorTitle={actionErrorTitle[action.action]}
      onPreview={() => showPreviewModal(emailMessage)}
    >
      <LabelTextGroup
        label={t('sendTo')}
        text={recipients}
        icon={<IconTextFormatLink color="elementPurple50" />}
      />
      <LabelTextGroup label={t('subject')} text={getRawTextFromHtml(action.data?.subject ?? '')} />
      <Stack space={1}>
        <Text font="semibold14" text={t('message')} />
        <HtmlContent
          font="regular14"
          html={action.savedValues?.contentHtml ?? action.data?.content ?? ''}
        />
      </Stack>

      {hasAttachments && (
        <Stack space={1}>
          <Text font="semibold14" text={t('emailAttachment')} />
          {status === 'completed' ? (
            <>
              {attachments.map((attachment, idx) => (
                <UploadedFile
                  key={idx}
                  isDisabled={!attachment.canView}
                  name={attachment.name}
                  path={attachment.src}
                  onPreview={() => {
                    showFilePreviewModal({ formFile: attachment, filesList: [attachment] });
                  }}
                />
              ))}
            </>
          ) : (
            <AttachmentList attachments={attachmentsValues} showAsCards />
          )}
        </Stack>
      )}
    </ActionResultCard>
  );
};

const sidebarLocale = {
  en: {
    sendEmail: 'Send email',
    details: 'Details',
    email: 'Email',
    dataNotFound: 'Data not found',
    description: 'Description',
    message: 'Message',
    emailSent: 'Email sent',
    emailSubject: 'Email subject',
    messageBody: 'Message (body)',
    sentTo: 'Sent to',
    retry: 'Retry',
    resendEmail: 'Resend email',
    subject: 'Email subject',
    subjecPlaceholder: 'Add subject',
    sendTo: 'Send to',
    inProgress: 'In progress',
    notSent: 'Not sent',
    error: 'Error',
    delivered: 'Delivered',
    emailMessage: 'Email message',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    emailContent: 'Email content',
    sendToInfo: 'One or more recipient for your email',
    subjectInfo: 'Subject of your email, with optional variables',
    emailAttachment: 'Email attachment',
  },
};
